import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";

import {
  useSensor,
  DndContext,
  useSensors,
  TouchSensor,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { useRedirectIfUnauthenticated } from "utils/azureAD/useRedirectIfUnauthenticated";
import { getSynchronizationItemsAsync } from "PdfDocumentGrid/pdfDocuments.service";
import { RESOURCES_KEYS } from "utils/i18n/resources";
import { useUpdateCategoriesOrderAsync } from "./apis";
import { SortableItem } from "./SortableItem";

export const SortableCategoriesUsingDragAndDrop: React.FunctionComponent =
  () => {
    const [sortedCategories, setSortedCategories] = useState<
      { id: string; name: string; categoryOrder: number }[]
    >([]);
    const { accounts } = useRedirectIfUnauthenticated();
    const { t: translate } = useTranslation();
    const username = accounts[0]?.username;

    const { isLoading: isGetSynchronizationItemsLoading } = useQuery(
      ["synchronizationItems", { isOnlyFav: false, username }],
      () =>
        getSynchronizationItemsAsync(username, { hasToSyncDocuments: false }),
      {
        cacheTime: 0,
        staleTime: 0,
        refetchOnReconnect: false,
        onSuccess: (synchronizationItemsData) => {
          const categoriesToSort = (
            synchronizationItemsData?.responseData?.items || []
          ).map(({ id, name, categoryOrder, ...rest }) => ({
            id,
            name,
            categoryOrder,
            externalId: [...rest.applicables, ...rest.nextApplicables],
          }));

          setSortedCategories(categoriesToSort);
        },
      }
    );

    const { mutateAsync: updateCategoriesOrder } =
      useUpdateCategoriesOrderAsync();

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 1,
          tolerance: 1,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      setSortedCategories((currentCategories) => {
        const oldIndex = currentCategories.findIndex(
          (item) => item.id === active.id
        );

        const newIndex = currentCategories.findIndex(
          (item) => item.id === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newCategoriesOrder = arrayMove(
            currentCategories,
            oldIndex,
            newIndex
          );

          const orderedCategoriesToSave = newCategoriesOrder.map(
            ({ id, name }, index) => ({ id, name, categoryOrder: index + 1 })
          );
          updateCategoriesOrder(orderedCategoriesToSave);

          return newCategoriesOrder;
        }

        return currentCategories;
      });
    };

    return (
      <Box>
        <Typography
          variant="h3"
          sx={{ width: "100%", textAlign: "center", mt: 2 }}
        >
          {translate(RESOURCES_KEYS.ORDER_CATEGORIES)}
        </Typography>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          flexDirection="row"
          justifyContent="center"
          alignContent={"flex-start"}
          gap={2}
          mt={2}
        >
          {isGetSynchronizationItemsLoading ? (
            <CircularProgress sx={{ m: "auto" }} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "start",
                flexDirection: "row",
                minWidth: "90%",
                maxHeight: "60vh",
                overflowY: "auto",
                p: 2,
                m: 2,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <DndContext
                sensors={sensors}
                onDragEnd={handleDragEnd}
                collisionDetection={closestCenter}
                autoScroll={true}
              >
                <SortableContext
                  items={sortedCategories}
                  strategy={rectSortingStrategy}
                >
                  {sortedCategories.map((item) => (
                    <SortableItem key={item.id} {...item} />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          )}
        </Box>
      </Box>
    );
  };
